import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, CalendarCheck, Bell, QrCode, ArrowRight, Clock } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Sales = () => {
  const [activeTab, setActiveTab] = useState("upcoming");

  return (
    <div data-testid="sales-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
                Estate Sales
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[#1C1914] leading-tight mb-6">
                Current & Past Estate Sales
              </h1>
              <p className="text-lg text-[#5C564D] leading-relaxed mb-8">
                Browse our upcoming estate sales in Fargo, Moorhead, and surrounding communities. Don't miss out on unique finds—subscribe to get notified about new sales!
              </p>
              <a
                href="tel:2182982420"
                className="inline-flex items-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-medium"
                data-testid="sales-phone-button"
              >
                <Phone weight="fill" className="w-5 h-5" />
                Call 218-298-2420
              </a>
            </div>

            {/* Subscribe Card */}
            <div className="bg-[#2C4C3B] p-8 md:p-10 rounded-lg text-white">
              <div className="flex items-center gap-3 mb-6">
                <Bell weight="duotone" className="w-8 h-8 text-[#C5A059]" />
                <h2 className="text-2xl font-serif font-medium">Never Miss a Sale!</h2>
              </div>
              <p className="text-white/80 mb-6">
                Subscribe to our mailing list and be the first to know about upcoming estate sales in your area. Get early access and exclusive notifications.
              </p>
              <div className="bg-white/10 p-6 rounded-lg mb-6">
                <div className="flex items-center gap-4">
                  <div className="bg-white p-3 rounded-lg">
                    <QrCode weight="duotone" className="w-16 h-16 text-[#2C4C3B]" />
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">Scan QR Code or click below</p>
                    <p className="text-white font-medium">Subscribe for Sale Alerts</p>
                  </div>
                </div>
              </div>
              <a
                href="https://estatesales.org/subscriber/company/add/25580"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-6 py-4 rounded-md transition-all duration-300 font-semibold w-full"
                data-testid="subscribe-button"
              >
                <Bell weight="fill" className="w-5 h-5" />
                Subscribe to Sale Notifications
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Sales Listings Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="upcoming" value={activeTab} onValueChange={setActiveTab}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
              <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914]">
                Estate Sale Listings
              </h2>
              <TabsList className="bg-[#F7F5F0] p-1 rounded-lg">
                <TabsTrigger 
                  value="upcoming" 
                  className="px-6 py-3 rounded-md data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white transition-all"
                  data-testid="tab-upcoming"
                >
                  <CalendarCheck weight="duotone" className="w-5 h-5 mr-2" />
                  Upcoming Sales
                </TabsTrigger>
                <TabsTrigger 
                  value="past" 
                  className="px-6 py-3 rounded-md data-[state=active]:bg-[#2C4C3B] data-[state=active]:text-white transition-all"
                  data-testid="tab-past"
                >
                  Past Sales
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="upcoming" className="mt-0">
              <div className="bg-white border border-[#E2DCD0] rounded-lg overflow-visible">
                <iframe 
                  src="/upcoming-sales.html"
                  title="Upcoming Estate Sales in Fargo ND, Moorhead MN - Legacy Liquidators LLC"
                  className="w-full border-0"
                  style={{ background: '#F7F5F0', minHeight: '300px', height: '300px' }}
                />
              </div>
            </TabsContent>

            <TabsContent value="past" className="mt-0">
              <div className="bg-white border border-[#E2DCD0] rounded-lg overflow-visible">
                <iframe 
                  src="/past-sales.html"
                  title="Past Estate Sales in Fargo ND, Moorhead MN - Legacy Liquidators LLC"
                  className="w-full border-0"
                  style={{ background: '#F7F5F0', minHeight: '300px', height: '300px' }}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Subscribe CTA Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#F7F5F0]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white border border-[#E2DCD0] p-8 md:p-12 rounded-lg text-center">
            <Bell weight="duotone" className="w-16 h-16 text-[#C5A059] mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-serif font-medium text-[#1C1914] mb-4">
              Stay Updated on Upcoming Sales
            </h2>
            <p className="text-[#5C564D] mb-8 max-w-lg mx-auto">
              Don't miss out on great finds! Subscribe to receive notifications about our upcoming estate sales throughout Fargo-Moorhead and the surrounding area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://estatesales.org/subscriber/company/add/25580"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#2C4C3B] hover:bg-[#1E3629] text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
                data-testid="cta-subscribe-button"
              >
                <Bell weight="fill" className="w-5 h-5" />
                Subscribe Now
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white px-8 py-4 rounded-md transition-all duration-300 font-semibold"
                data-testid="cta-contact-button"
              >
                Schedule a Sale
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* For Sellers Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 lg:px-24 bg-[#2C4C3B]">
        <div className="max-w-4xl mx-auto text-center">
          <span className="text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-[#C5A059] block mb-4">
            For Families
          </span>
          <h2 className="text-3xl sm:text-4xl font-serif font-medium text-white mb-6">
            Need Help With an Estate Sale?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Legacy Liquidators LLC has been serving families for 9 years. We handle everything—no need to clean first. Contact us for a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:2182982420"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] hover:bg-[#A88746] text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="seller-phone-button"
            >
              <Phone weight="fill" className="w-5 h-5" />
              Call 218-298-2420
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1C1914] px-8 py-4 rounded-md transition-all duration-300 font-semibold"
              data-testid="seller-contact-button"
            >
              Request a Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sales;
